import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { withTranslation } from "react-i18next";
import { toast } from "react-toastify";
import PropTypes from "prop-types";
import { isEqual, omit } from "lodash";
import { useFormik } from "formik";

import Stepper from "common/Stepper";
import AuthorizedPersonDetails from "Components/AuthorizedPersonDetails/AuthorizedPersonDetails";
import DocumentToSubmit from "Components/DocumentsToSubmit";
import Declaration from "Components/Declaration";
import BreadCrumb from "Components/Common/BreadCrumb";
import Backdrop from "Components/Common/Backdrop";
import BackupPowerDetail from "./BackupPowerDetail";
import ConnectionLoadDetails from "./ConnectionLoadDetails";
import { applicationTypes, minHtContractDemandLoad } from "common/constant";
import { getOptionValue } from "common/enums";

import { useValidationSchemaBackupPower } from "hooks";
import { setValues } from "slices/BackupPower/reducer";

import {
  convertDocumentKeys,
  filterEmptyStrings,
  generateFormData,
} from "utils";
import { getIsDocumentRequired } from "utils/getIsDocumentRequired";

import { useLazyConsumerByIdQuery } from "apis/User";
import {
  useLazyApplicationDetailQuery,
  usePostApplicationDetailsMutation,
} from "apis/ApplicationRequestDetail";
import { myRequestRoute } from "Routes/constants";

const BackupPower = ({ applicationId, handleClose = () => {}, t }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const [applicationData, setApplicationData] = useState({});

  const user = useSelector((state) => state.Login.user);

  const navigate = useNavigate();

  const [submitApplication, { isLoading }] =
    usePostApplicationDetailsMutation();

  const [
    getUtilityConsumerDetails,
    {
      data: { data: customerDetails } = {},
      isLoading: isConsumerDetailsLoading,
      isFetching: isConsumerDataFetching,
    },
  ] = useLazyConsumerByIdQuery();

  const [
    getApplicationDetails,
    {
      isLoading: isApplicationDataLoading,
      isFetching: isApplicationDataFetching,
    },
  ] = useLazyApplicationDetailQuery();

  const getApplicationData = async (applicationId) => {
    try {
      const res = await getApplicationDetails(applicationId);
      setApplicationData(res?.data);
    } catch (error) {}
  };

  const getValidationSchema = useValidationSchemaBackupPower();

  const dispatch = useDispatch();
  const initialValues = useMemo(() => {
    return {
      id: applicationId || 0,
      utilityConsumerId:
        applicationData?.utilityConsumerId ?? customerDetails?.id ?? "",
      utilityId: 1,
      consumerNumber:
        applicationData?.consumerNumber ??
        customerDetails?.consumerNumber ??
        "",
      applicationTypeId: applicationTypes["Backup Power"],
      applicant: {
        id:
          applicationData?.applicant?.id ?? customerDetails?.applicant?.id ?? 0,
        applicantTypeId: customerDetails?.applicant?.applicantType?.id ?? "",
        applicantName: customerDetails?.applicant?.applicantName ?? "",
        panNumber:
          applicationData?.applicant?.panNumber ??
          customerDetails?.applicant?.panNumber ??
          "",
        gstNumber:
          applicationData?.applicant?.gstNumber ??
          customerDetails?.applicant?.gstNumber ??
          "",
        occupancyTypeId: customerDetails?.applicant?.occupancyType?.id ?? "",
        isGSTRegistered:
          applicationData?.applicant?.isGSTRegistered ??
          customerDetails?.applicant?.isGSTRegistered ??
          "",
        addressType: customerDetails?.applicant?.addressType ?? "",
        correspondenceAddress:
          customerDetails?.applicant?.correspondenceAddress ?? "",
        city: customerDetails?.applicant?.city ?? "",
        pinCode: customerDetails?.applicant?.pinCode ?? "",
        email: customerDetails?.applicant?.email ?? "",
        // isEmailVerified: !!applicationData?.applicant?.email ?? false,
        mobileNumber: customerDetails?.applicant?.mobileNumber ?? "",
        landLine: customerDetails?.applicant?.landLine ?? "",
        alternateEmail: customerDetails?.applicant?.alternateEmail
          ? customerDetails?.applicant?.alternateEmail?.split(", ")
          : [],
        alternateMobileNumber:
          customerDetails?.applicant?.alternateMobileNumber ?? "",
        eBillConfirmation: true,
      },
      authorizedPerson: {
        isAuthorizedPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email ===
              applicationData.user.email &&
            applicationData.authorizedPerson.mobileNumber ===
              applicationData.user.mobileNumber
          : true,
        isEditablePerson:
          applicationData?.authorizedPerson?.isEditablePerson ?? false,
        id: applicationData?.authorizedPerson?.id ?? 0,
        salutation: applicationData?.authorizedPerson?.salutation ?? "",
        firstName: applicationData?.authorizedPerson?.firstName ?? "",
        middleName: applicationData?.authorizedPerson?.middleName ?? "",
        lastName: applicationData?.authorizedPerson?.lastName ?? "",
        designation: applicationData?.authorizedPerson?.designation ?? "",
        email: applicationData?.authorizedPerson?.email ?? "",
        mobileNumber: applicationData?.authorizedPerson?.mobileNumber ?? "",
        landLine: applicationData?.authorizedPerson?.landLine ?? "",
        image: "",
        imageUrl: applicationData?.authorizedPerson?.imageUrl ?? "",
        isUnRegisteredPerson: applicationData?.authorizedPerson
          ? applicationData.authorizedPerson.email !== user.email &&
            applicationData.authorizedPerson.mobileNumber !== user.mobileNumber
          : false,
      },
      utilityAddress: {
        premisesAreaId: customerDetails?.utilityAddress?.premiseArea?.id ?? "",
      },

      connectionLoad: {
        id: applicationData?.connectionLoad?.id ?? 0,
        backupRequired:
          !!applicationData?.connectionLoad?.backupkVA ||
          !!customerDetails?.connectionLoad?.backupkVA,
        backupkVA: applicationId
          ? applicationData?.connectionLoad?.backupkVA ?? ""
          : customerDetails?.connectionLoad?.backupkVA ?? "",

        backupPowerAlteration: applicationId
          ? applicationData?.connectionLoad?.backupPowerAlteration ?? ""
          : customerDetails?.connectionLoad?.backupkVA
          ? 2
          : 1,
        loadDetails:
          applicationData?.connectionLoad?.loadDetail ??
          customerDetails?.connectionLoad?.loadDetail ??
          "",

        supplyVoltage: applicationData?.connectionLoad?.supplyVoltage
          ? +applicationData.connectionLoad.supplyVoltage
          : customerDetails?.connectionLoad?.supplyVoltage
          ? +customerDetails.connectionLoad.supplyVoltage
          : "",
        supplyVoltageUnit:
          applicationData?.connectionLoad?.supplyVoltageUnit ??
          customerDetails?.connectionLoad?.supplyVoltageUnit ??
          "",
        powerRequirement:
          applicationData?.connectionLoad?.totalPowerRequirement ??
          customerDetails?.connectionLoad?.totalPowerRequirement ??
          "",
        powerRequirementUnit:
          (applicationData?.connectionLoad?.totalPowerRequirement ??
            customerDetails?.connectionLoad?.totalPowerRequirement) <= 99
            ? 1
            : 2 ?? "",
        timeShift:
          applicationData?.connectionLoad?.timeShiftEnum ??
          customerDetails?.connectionLoad?.timeShiftEnum ??
          "",
        tention: applicationData
          ? applicationData?.connectionLoad?.totalPowerRequirement
            ? applicationData.connectionLoad.totalPowerRequirement <
              minHtContractDemandLoad
              ? "LT"
              : "HT"
            : ""
          : customerDetails?.connectionLoad?.totalPowerRequirement
          ? customerDetails.connectionLoad.totalPowerRequirement <
            minHtContractDemandLoad
            ? "LT"
            : "HT"
          : "",
        totalPowerRequirement:
          applicationData?.connectionLoad?.totalPowerRequirement ??
          customerDetails?.connectionLoad?.totalPowerRequirement ??
          0,
        isGreenPowerRequired: !!customerDetails?.connectionLoad?.greenPower,
        greenPower: customerDetails?.connectionLoad?.greenPower ?? "",
        averageConsumption:
          customerDetails?.connectionLoad?.averageConsumption ?? "",
      },
      declaration: {
        declarationId: applicationData?.declaration?.id ?? 0,
        isConsumerDuesOutstanding:
          !!applicationData?.declaration?.consumerDuesOutstanding ?? false,
        consumerDuesOutstanding:
          applicationData?.declaration?.consumerDuesOutstanding ?? "",
        isPremisesDuesOutstanding:
          !!applicationData?.declaration?.premisesDuesOutstanding ?? false,
        premisesDuesOutstanding:
          applicationData?.declaration?.premisesDuesOutstanding ?? "",
        isFirmDuesOutstanding:
          !!applicationData?.declaration?.firmDuesOutstanding ?? false,
        firmDuesOutstanding:
          applicationData?.declaration?.firmDuesOutstanding ?? "",
      },
      documents: applicationData?.documents?.filter(({ id }) => id) ?? [],

      extraValues: applicationData?.extraValues ?? {
        exisingbackupkVA: customerDetails?.connectionLoad?.backupkVA || "",
        premiseUsagePurpose:
          customerDetails?.utilityAddress?.premiseUsagePurpose ?? "",
        consumerName: customerDetails?.consumerName,
        tariffName: customerDetails?.tariffName ?? "",
        requiredDocumentsCount: 0,
        isPanNumber: !!customerDetails?.applicant?.panNumber,
        isGstNumber: !!customerDetails?.applicant?.gstNumber,
        userMobileNumber: applicationData?.user?.mobileNumber ?? "",
        userEmail: applicationData?.user?.email ?? "",
        // premiseType: customerDetails?.utilityAddress?.premiseType?.name ?? "",
      },
      inProgressApplicationId: customerDetails?.inProgressApplicationId ?? 0,
      payableBillAmount: customerDetails?.payableBillAmount ?? 0,
      isAllApplicationsProcessCompleted:
        customerDetails?.isAllApplicationsProcessCompleted ?? false,
      isIGSTExempted: customerDetails?.isIGSTExempted ?? false,
    };
  }, [applicationData, customerDetails]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: getValidationSchema(currentStep),

    onSubmit: async (values, actions) => {
      if (currentStep === steps.length - 1) {
        createRequestForBackPower(values);
      } else {
        if (
          isEqual(
            values?.connectionLoad?.backupkVA,
            customerDetails?.connectionLoad?.backupkVA
          ) &&
          isEqual(
            values?.connectionLoad?.backupPowerAlteration,
            getOptionValue("powerAlterationOptions", "ChangeLoad")
          ) &&
          currentStep === 1
        ) {
          toast.warning("Please change load details");
        } else if (
          values?.inProgressApplicationId !== applicationData?.id &&
          !values?.isAllApplicationsProcessCompleted
        ) {
          toast.error(
            "There are some incomplete applications associated with this consumer number. You are unable to fill the form."
          );
        } else if (values.payableBillAmount > 0) {
          toast.error(
            `You have an unpaid amount of ${values.payableBillAmount} RS. Please pay first to continue.`
          );
        }
        // else if (
        //   values?.extraValues?.premiseType?.toLowerCase() !== "residential"
        // )
        else {
          setCurrentStep((prev) => prev + 1);
          actions.setTouched({});
        }
      }
    },
  });

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: "Backup Power",
        isCompleted: currentStep >= 1,
        component: <BackupPowerDetail formik={formik} />,
      },
      {
        id: 2,
        name: "Connection Load",
        isCompleted: currentStep >= 2,
        component: <ConnectionLoadDetails formik={formik} />,
      },
      {
        id: 3,
        name: "Authorized Person Details",
        isCompleted: currentStep >= 3,
        component: <AuthorizedPersonDetails formik={formik} />,
      },
      {
        id: 4,
        name: "Document to Submit",
        isCompleted: currentStep >= 4,
        component: <DocumentToSubmit formik={formik} />,
      },
      {
        id: 5,
        name: "Declaration",
        isCompleted: currentStep === 5,
        component: <Declaration formik={formik} />,
      },
    ],
    [currentStep, formik, customerDetails]
  );

  const { values, initialValues: formikInitialValues, handleSubmit } = formik;

  const {
    documents,
    extraValues: { requiredDocumentsCount },
  } = values;

  useEffect(() => {
    if (applicationId) {
      getApplicationData(applicationId);
      // setCompletedSteps(steps.length);
    }
  }, [applicationId]);

  useEffect(() => {
    dispatch(setValues(values));
  }, [values, dispatch]);

  const getConsumerDetails = async (utilityConsumerId) => {
    try {
      await getUtilityConsumerDetails(utilityConsumerId);
      if (
        applicationId &&
        values.utilityConsumerId !== formikInitialValues.utilityConsumerId
      ) {
        setApplicationData({});
      }
    } catch (error) {}
  };

  useEffect(() => {
    if (values.utilityConsumerId) {
      getConsumerDetails(values.utilityConsumerId);
    }
  }, [values.utilityConsumerId]);

  const validateDocument = () => {
    const requiredUploadedDocumentsCount = documents.filter(
      ({ documentRequiredEnum }) => getIsDocumentRequired(documentRequiredEnum)
    ).length;

    if (
      currentStep === 3 &&
      requiredUploadedDocumentsCount !== requiredDocumentsCount
    ) {
      toast.error("Please upload all required documents");
    }
  };

  const createRequestForBackPower = async (values) => {
    const imageKeyToRemove = values.authorizedPerson.image
      ? "authorizedPerson.imageUrl"
      : "authorizedPerson.image";
    const cityId = values.applicant.city?.id ?? "";
    const id = values.applicant.id;

    // converts document array to key-value pair
    const payloadWithUpdatedDocumentKeys = convertDocumentKeys(values);

    const applicationDetails = omit(payloadWithUpdatedDocumentKeys, [
      "applicant.city",
      "connectionLoad.backupRequired",
      "connectionLoad.tention",
      "inProgressApplicationId",
      "isAllApplicationsProcessCompleted",
      "payableBillAmount",
      "consumerNumber",
      "connectionLoad.isGreenPowerRequired",
      "authorizedPerson.isAuthorizedPerson",
      "authorizedPerson.isUnRegisteredPerson",
      "extraValues",
      "declaration.isConsumerDuesOutstanding",
      "declaration.isPremisesDuesOutstanding",
      "declaration.isFirmDuesOutstanding",
      "documents",
      "isIGSTExempted",
      "authorizedPerson.isEditablePerson",
      imageKeyToRemove,
    ]);

    const existingBackup = customerDetails?.connectionLoad?.backupkVA;
    const updatedBackup =
      applicationDetails.connectionLoad.backupPowerAlteration === 3
        ? ""
        : values?.connectionLoad?.backupkVA;

    if (existingBackup === updatedBackup) {
      return toast.warning("Please update Backup Power");
    }

    applicationDetails.connectionLoad.backupkVA = updatedBackup;

    const currentGstNumber = applicationId
      ? applicationData?.applicant?.gstNumber
      : customerDetails?.applicant?.gstNumber;

    const currentPanNumber = applicationId
      ? applicationData?.applicant?.panNumber
      : customerDetails?.applicant?.panNumber;

    const filteredValues = filterEmptyStrings({
      ...applicationDetails,
      applicant: {
        ...applicationDetails.applicant,
        alternateEmail: applicationDetails.applicant.alternateEmail.length
          ? applicationDetails.applicant.alternateEmail.join(", ")
          : "",
        cityId,
        id:
          applicationDetails.applicant.gstNumber !== (currentGstNumber ?? "") ||
          applicationDetails.applicant.panNumber !== (currentPanNumber ?? "")
            ? 0
            : id,
      },
    });

    // remove entries that contain only id field
    const payload = Object.entries(filteredValues).reduce(
      (res, [key, value]) => {
        if (
          typeof value !== "object" ||
          value instanceof File ||
          (value && Object.keys(value).length > 1)
        ) {
          return { ...res, [key]: value };
        }
        return res;
      },
      {}
    );

    const formData = new FormData();
    generateFormData(formData, {
      ...payload,
      declaration: filteredValues.declaration,
    });

    try {
      await submitApplication(formData);
      handleClose();
      if (!applicationId) {
        navigate(myRequestRoute);
      }
    } catch (error) {}
  };
  return (
    <div className={!applicationId ? "h-100" : ""}>
      <Backdrop
        open={
          isApplicationDataLoading ||
          isConsumerDetailsLoading ||
          isApplicationDataFetching ||
          isConsumerDataFetching ||
          (!!applicationId && !customerDetails)
        }
      />
      {!applicationId && (
        <BreadCrumb
          title={t("Backup Power")}
          pageTitle={t("Power Applications")}
        />
      )}
      <div className="stepper-container">
        <Stepper
          steps={steps}
          handleSubmit={handleSubmit}
          activeStepIndex={currentStep}
          setActiveStepIndex={setCurrentStep}
          validateDocument={validateDocument}
          isLoading={isLoading}
          formik={formik}
          isDraftDisabled
        />
      </div>
    </div>
  );
};
BackupPower.propTypes = {
  applicationId: PropTypes.number,
  handleClose: PropTypes.func,
  t: PropTypes.func,
};

export default withTranslation()(BackupPower);
